import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, CircularProgress, Alert, Divider, FormControl, InputLabel, MenuItem, Select,
  TextField, Typography, Avatar, IconButton, Tooltip, Checkbox, FormControlLabel, FormGroup, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { API_BASE } from '../config';
import usePageTitle from '../shared/hooks/usePageTitle';
import WorkshopFieldsEditor from '../shared/WorkshopFieldsEditor';
import SizeDietaryFields from '../shared/SizeDietaryFields';
import { toPrintableImage } from '../shared/imageUtils';
import { ALLOWED_SESSIONS, PHOTO_ACCEPT, MAX_PHOTO_BYTES, emptyWorkshop } from '../shared/workshopOptions';

// Public, no-login page a workshop leader reaches through their private
// link (copied from Admin › Workshops › Workshop Leaders). They fill in their
// bio, photo, availability and workshop details; the admin reviews it. The
// backend only ever returns this leader's own editable information — never
// pay, contract or admin notes.
export default function WorkshopLeaderForm() {
  usePageTitle('OSSDF Workshop Leader Information');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const q = `?token=${encodeURIComponent(token)}`;

  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!token) { setLoadError('This link is missing its code — please use the full link you were sent.'); return; }
    try {
      const res = await fetch(`${API_BASE}/api/workshop-leader/me${q}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'This link is invalid or has expired.');
      setInfo(body);
      setPhotoUrl(body.profile.photoUrl);
      setForm({
        name: body.profile.name || '',
        phone: body.profile.phone || '',
        bio: body.profile.bio || '',
        tshirtSize: body.profile.tshirtSize || '',
        dietary: body.profile.dietary || '',
        // Only sent (and only saved) when the admin lets them choose.
        ...(body.leaderChoosesSessions ? { allowedSessions: body.allowedSessions } : {}),
        // A blank one to start from — unless they can't add workshops, in
        // which case they only fill in the ones OSSDF added.
        workshops: body.workshops.length
          ? body.workshops.map(w => ({ ...emptyWorkshop(), ...w }))
          : (body.canAddWorkshops ? [emptyWorkshop()] : []),
      });
    } catch (err) {
      setLoadError(err.message);
    }
  }, [token, q]);
  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => { setSaved(false); setForm(f => ({ ...f, [key]: e.target.value })); };
  const setWorkshop = (i) => (value) => { setSaved(false); setForm(f => ({ ...f, workshops: f.workshops.map((w, j) => (j === i ? value : w)) })); };
  const addWorkshop = () => setForm(f => ({ ...f, workshops: [...f.workshops, emptyWorkshop()] }));
  const removeWorkshop = (i) => setForm(f => ({ ...f, workshops: f.workshops.filter((_, j) => j !== i) }));

  const handlePhoto = async (picked) => {
    setSaveError(null);
    // WebP / very large photos become a JPEG so they can print in the bios PDF.
    const file = picked && await toPrintableImage(picked);
    if (file && (!PHOTO_ACCEPT.split(',').includes(file.type) || file.size > MAX_PHOTO_BYTES)) {
      setSaveError('Photos must be JPG, PNG or WebP, 5MB or smaller.');
      return;
    }
    setPhotoBusy(true);
    try {
      let key = null;
      if (file) {
        const r = await fetch(`${API_BASE}/api/workshop-leader/photo-upload-url${q}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contentType: file.type, size: file.size }),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || 'Could not start the upload');
        const put = await fetch(body.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        if (!put.ok) throw new Error('Photo upload failed — please try again');
        key = body.key;
      }
      const r = await fetch(`${API_BASE}/api/workshop-leader/photo${q}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Could not save the photo');
      setPhotoUrl(body.photoUrl);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true); setSaveError(null); setSaved(false);
    try {
      const res = await fetch(`${API_BASE}/api/workshop-leader/me${q}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Save failed (${res.status})`);
      setSaved(true);
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const canAdd = info?.canAddWorkshops !== false;
  const canSave = form && form.name.trim() && (form.workshops.length > 0 || !canAdd) && form.workshops.every(w => w.title.trim());

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>Workshop leader information</Typography>
      {loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : !form ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Thank you for leading a workshop at {info.eventName || 'DanceFest'}{info.eventDates ? ` (${info.eventDates})` : ''}!
            Please share a short bio, a photo and a description of your workshop — we post these for the schools and dancers.
            You can come back to this link and update it any time.
          </Typography>
          {info.status === 'Info Complete' && <Alert severity="success" sx={{ mb: 2 }}>You’re confirmed and we have everything we need. Thank you!</Alert>}
          {info.status !== 'Info Complete' && info.missingInfo?.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {info.status === 'Confirmed' ? 'You’re confirmed — thank you! ' : ''}
              Still needed from you: <strong>{info.missingInfo.join(', ')}</strong>.
              {info.missingInfo.includes('Payment preference') && ' (You can choose how to be paid once we send your contract.)'}
            </Alert>
          )}
          <ContractSection token={token} />

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography fontWeight={600} sx={{ mb: 2 }}>About you</Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Avatar src={photoUrl || undefined} variant="rounded" sx={{ width: 120, height: 120, fontSize: 40 }}>{form.name?.[0]}</Avatar>
                  <input ref={fileRef} type="file" accept={PHOTO_ACCEPT} hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handlePhoto(f); }} />
                  <Button size="small" onClick={() => fileRef.current?.click()} disabled={photoBusy}>{photoBusy ? 'Uploading…' : photoUrl ? 'Replace photo' : 'Upload photo'}</Button>
                  {photoUrl && <Button size="small" color="error" onClick={() => handlePhoto(null)} disabled={photoBusy}>Remove</Button>}
                  <Typography variant="caption" color="text.secondary">JPG, PNG or WebP, up to 5MB</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 240, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField label="Your name" size="small" required value={form.name} onChange={set('name')} inputProps={{ maxLength: 120 }} />
                  <TextField label="Phone" size="small" value={form.phone} onChange={set('phone')} inputProps={{ maxLength: 40 }} />
                  <TextField label="Bio" size="small" multiline minRows={5} value={form.bio} onChange={set('bio')} inputProps={{ maxLength: 3000 }}
                    helperText="A short bio we can post with your photo." sx={{ gridColumn: '1 / -1' }} />
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <SizeDietaryFields value={form} onChange={(changes) => { setSaved(false); setForm(f => ({ ...f, ...changes })); }} />
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Sessions</Typography>
              {info.leaderChoosesSessions ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    There are two workshop sessions: {sessionText(info, 1)} and {sessionText(info, 2)}. When are you available to teach?
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>I’m available for</InputLabel>
                    <Select label="I’m available for" value={form.allowedSessions} onChange={set('allowedSessions')}>
                      {ALLOWED_SESSIONS.map(s => <MenuItem key={s} value={s}>{AVAILABILITY_LABELS[s]}</MenuItem>)}
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <Typography variant="body2">{bookedSessionsText(info)}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    If this doesn’t look right, please contact the OSSDF workshop coordinator.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography fontWeight={600}>Your workshop{form.workshops.length === 1 ? '' : 's'}</Typography>
                {canAdd && form.workshops.length < info.maxWorkshops && (
                  <Button size="small" startIcon={<AddIcon />} onClick={addWorkshop}>Add another workshop</Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {!canAdd
                  ? (form.workshops.length
                    ? 'Please fill in the details for the workshop(s) we’ve planned with you.'
                    : 'We’re still planning your workshop — we’ll add it here for you to fill in the details.')
                  : (form.allowedSessions ?? info.allowedSessions) === 'Both Sessions'
                    ? 'You can run the same workshop in both sessions, or describe a different one for each.'
                    : 'You can suggest more than one workshop and we’ll choose one.'}
              </Typography>
              {form.workshops.map((w, i) => (
                <Box key={w.workshopId || `new-${i}`}>
                  {i > 0 && <Divider sx={{ my: 2 }} />}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Workshop {i + 1}</Typography>
                    {canAdd && form.workshops.length > 1 && (
                      <Tooltip title="Remove this workshop">
                        <IconButton size="small" onClick={() => removeWorkshop(i)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <WorkshopFieldsEditor value={w} onChange={setWorkshop(i)} equipmentOptions={info.equipmentOptions} />
                </Box>
              ))}
            </CardContent>
          </Card>

          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 2 }}>Saved — thank you! You can come back to this link to make changes.</Alert>}
          <Button variant="contained" size="large" onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save my information'}
          </Button>
        </>
      )}
    </Box>
  );
}

// "You're booked for …" from the session(s) OSSDF set for this leader, with
// the session names/times from the Schedule page's settings.
const sessionText = ({ sessions }, n) => `${sessions?.[n]?.name || `Session ${n}`}${sessions?.[n]?.time ? ` (${sessions[n].time})` : ''}`;

// When the leader picks their own availability.
const AVAILABILITY_LABELS = {
  Either: 'One session — either one works',
  'Both Sessions': 'Both sessions',
  'Session 1': 'Session 1 only',
  'Session 2': 'Session 2 only',
};

function bookedSessionsText(info) {
  const { allowedSessions } = info;
  const s = (n) => sessionText(info, n);
  if (allowedSessions === 'Both Sessions') return `You’re booked to teach both workshop sessions: ${s(1)} and ${s(2)}.`;
  if (allowedSessions === 'Session 1') return `You’re booked to teach ${s(1)}.`;
  if (allowedSessions === 'Session 2') return `You’re booked to teach ${s(2)}.`;
  return `You’re booked to teach one workshop session — ${s(1)} or ${s(2)}. We’ll confirm which one.`;
}

// The contract, drawn from the blocks the backend builds (backend/richText.js):
// paragraphs and bullets with bold / italic / underline / size, matching the
// PDF. Rendered as plain React text — never as HTML.
function ContractText({ blocks }) {
  return (
    <Box sx={{ typography: 'body2', lineHeight: 1.6 }}>
      {(blocks || []).map((bl, i) => {
        if (!bl.runs.length) return <Box key={i} sx={{ height: 10 }} />;
        const runs = bl.runs.map((r, j) => (
          <Box component="span" key={j} sx={{
            fontWeight: r.b ? 700 : undefined, fontStyle: r.i ? 'italic' : undefined,
            textDecoration: r.u ? 'underline' : undefined, fontSize: r.size && r.size !== 11 ? `${r.size / 11}em` : undefined,
          }}>{r.text}</Box>
        ));
        if (bl.type !== 'li') return <Box key={i} sx={{ whiteSpace: 'pre-wrap' }}>{runs}</Box>;
        return (
          <Box key={i} sx={{ display: 'flex', gap: 1, pl: bl.level ? 4.5 : 1.5 }}>
            <Box component="span" sx={{ flexShrink: 0 }}>{bl.level ? '–' : '•'}</Box>
            <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{runs}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

// How the leader wants to be paid — e-transfer or cheque (one or the other).
// For e-transfer they confirm the email, which starts as the one OSSDF has
// for them. Saved on its own, before or after signing.
function PaymentChoice({ q, initial }) {
  const [method, setMethod] = useState(initial.paymentMethod || '');
  const [email, setEmail] = useState(initial.etransferEmail || '');
  const [saved, setSaved] = useState({ method: initial.paymentMethod || '', email: initial.etransferEmail || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim());
  const changed = method !== saved.method || (method === 'E-transfer' && email.trim() !== saved.email);
  const choose = (m) => { setOk(false); setMethod(prev => (prev === m ? '' : m)); };

  const save = async () => {
    setBusy(true); setError(null); setOk(false);
    try {
      const res = await fetch(`${API_BASE}/api/workshop-leader/payment${q}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethod: method, etransferEmail: email.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not save — please try again');
      setSaved({ method, email: email.trim() });
      setOk(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>How would you like to be paid?</Typography>
      <FormGroup row sx={{ alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
        {['E-transfer', 'Cheque'].map(m => (
          <FormControlLabel key={m} sx={{ mt: 0.25 }} control={<Checkbox checked={method === m} onChange={() => choose(m)} />}
            label={<Typography variant="body2">{m}</Typography>} />
        ))}
        {method === 'E-transfer' && (
          <TextField size="small" type="email" label="Send my e-transfer to" value={email} sx={{ flex: 1, minWidth: 240 }}
            error={!!email && !emailValid}
            helperText={email && !emailValid ? 'That email address doesn’t look right' : 'Please check this is right, or change it'}
            inputProps={{ maxLength: 200 }} onChange={e => { setOk(false); setEmail(e.target.value); }} />
        )}
        {/* Same height as the email box so they line up. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: 40 }}>
          <Button size="small" variant="outlined" onClick={save} sx={{ whiteSpace: 'nowrap' }}
            disabled={busy || !method || !changed || (method === 'E-transfer' && !emailValid)}>
            {busy ? <CircularProgress size={18} /> : 'Save payment details'}
          </Button>
          {ok && <Typography variant="body2" color="success.main" sx={{ whiteSpace: 'nowrap' }}>Saved ✓</Typography>}
        </Box>
      </FormGroup>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
    </Box>
  );
}

// The leader's contract — shown once OSSDF has sent it. They can read it,
// download the PDF, then either sign online (type their full name + agree)
// or upload a copy they signed on paper / in a PDF app.
function ContractSection({ token }) {
  const q = `?token=${encodeURIComponent(token)}`;
  const [contract, setContract] = useState(null);
  const [fullName, setFullName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState('');
  const fileRef = useRef(null);
  const sectionRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/workshop-leader/contract${q}`);
      const body = await res.json().catch(() => ({}));
      if (res.ok) setContract(body);
    } catch { /* the rest of the page still works */ }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  // Arriving from the "Send contract" email (#contract) scrolls straight here.
  useEffect(() => {
    if (contract?.available && window.location.hash === '#contract') sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [contract]);

  if (!contract?.available) return null;
  const signed = contract.status === 'Signed';
  const signedOn = contract.signedAt ? new Date(contract.signedAt).toLocaleDateString('en-CA', { dateStyle: 'long' }) : '';

  const post = async (path, method, payload) => {
    const res = await fetch(`${API_BASE}${path}${q}`, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || 'Something went wrong — please try again');
    return body;
  };

  const sign = async () => {
    setBusy(true); setError(null);
    try { await post('/api/workshop-leader/contract/sign', 'POST', { fullName: fullName.trim(), agree }); setDone('Thank you — your contract is signed.'); await load(); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const upload = async (file) => {
    setError(null);
    if (file.type !== 'application/pdf' || file.size > 10 * 1024 * 1024) { setError('Please upload your signed contract as a PDF, 10MB or smaller.'); return; }
    setBusy(true);
    try {
      const { uploadUrl, key } = await post('/api/workshop-leader/contract/upload-url', 'POST', { size: file.size });
      const put = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });
      if (!put.ok) throw new Error('Upload failed — please try again');
      await post('/api/workshop-leader/contract/upload', 'PUT', { key });
      setDone('Thank you — your signed contract was uploaded.');
      await load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const pdfUrl = `${API_BASE}/api/workshop-leader/contract.pdf${q}`;

  return (
    <Card variant="outlined" sx={{ mb: 3, borderColor: signed ? 'success.main' : 'primary.main' }} ref={sectionRef} id="contract">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography fontWeight={600}>Your contract</Typography>
          <Button size="small" href={pdfUrl} target="_blank" rel="noopener noreferrer">{signed ? 'Download signed copy' : 'Download PDF'}</Button>
        </Box>
        <PaymentChoice q={q} initial={contract} />
        <Divider sx={{ my: 2 }} />
        {done && <Alert severity="success" sx={{ mb: 2 }}>{done}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {signed ? (
          <Alert severity="success">
            {contract.signedName ? `Signed online by ${contract.signedName}${signedOn ? ` on ${signedOn}` : ''}.` : `Signed copy received${signedOn ? ` on ${signedOn}` : ''}.`}
            {' '}If anything needs to change, reply to the contract email.
          </Alert>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 360, overflow: 'auto', bgcolor: 'background.default', mb: 2 }}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>{contract.title}</Typography>
              <ContractText blocks={contract.blocks} />
            </Paper>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Sign online</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
              <TextField size="small" label="Type your full name" value={fullName} onChange={e => setFullName(e.target.value)} inputProps={{ maxLength: 120 }} sx={{ minWidth: 260 }} />
              <FormControlLabel control={<Checkbox checked={agree} onChange={e => setAgree(e.target.checked)} />}
                label={<Typography variant="body2">I have read and agree to this contract</Typography>} />
            </Box>
            <Button variant="contained" onClick={sign} disabled={busy || fullName.trim().length < 2 || !agree}>
              {busy ? <CircularProgress size={20} color="inherit" /> : 'Sign contract'}
            </Button>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Prefer to sign on paper or in a PDF app? Download the PDF above, sign it, and upload it here.
            </Typography>
            <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }} />
            <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()} disabled={busy}>Upload signed PDF</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
